import { useEffect, useState } from 'react';
import { SiteApi } from '../../api/SiteApi';
import { Features, SiteState } from '../../context/SiteContext';
import { GoogleAnalytics } from '../../classes/GoogleAnalytics';
import { CommonValidation } from '../common/commonValidation/commonValidation';
import i18n from 'i18next';

type Brand = {
    brandId: number;
    googleAnalyticsTrackingId: string;
    brandStyles: string;
};

type SiteSettings = {
    brand: Brand;
    clientId: string;
    domain: string;
    audience: string;
    features: Features;
    language: string;
};

const initialSiteSettings = {
    brand: {
        brandId: 0,
        googleAnalyticsTrackingId: '',
        brandStyles: ''
    },
    clientId: '',
    domain: '',
    audience: '',
    features: { Internationalization: 'off', GapClaim: 'off' },
    language: 'en'
};

function useSiteState(): SiteState {
    const [siteSettings, setSiteSettings] =
        useState<SiteSettings>(initialSiteSettings);
    const [brandSearchComplete, setBrandSearchComplete] = useState(false);

    const { brandId, brandStyles } = siteSettings.brand;
    const { clientId, domain, audience, features, language } = siteSettings;

    useEffect(() => {
        let mounted = true;

        const getSiteSettings = () => {
            const successCallback = (foundSettings: SiteSettings) => {
                if (mounted) {
                    const trackingId =
                        foundSettings.brand.googleAnalyticsTrackingId;

                    if (CommonValidation.isNotEmpty(trackingId)) {
                        GoogleAnalytics.initialise(trackingId);
                    }

                    i18n.changeLanguage(foundSettings.language);
                    localStorage.lang = foundSettings.language;
                    setSiteSettings(foundSettings);
                    setBrandSearchComplete(true);
                }
            };

            const failureCallback = () => {
                if (mounted) {
                    console.log('Failed to find brand');
                    setBrandSearchComplete(true);
                }
            };

            new SiteApi().getSiteSettings(
                window.location.hostname,
                window.location.pathname.split('/')[1] === 'redirect'
                    ? localStorage.lang
                    : window.location.pathname.split('/')[1],
                successCallback,
                failureCallback
            );
        };

        getSiteSettings();

        return function cleanup() {
            mounted = false;
        };
    }, []);

    return {
        brandId,
        brandStyles,
        brandSearchComplete,
        clientId,
        domain,
        audience,
        features,
        language
    };
}

export default useSiteState;
